import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, Loader2, X } from 'lucide-react';

// Step Components
import WelcomeStep from '../components/onboarding/WelcomeStep';
import SiteSetupStep from '../components/onboarding/SiteSetupStep';
import LanguageStep from '../components/onboarding/LanguageStep';
import IntegrationStep from '../components/onboarding/IntegrationStep';
import CodeSnippetStep from '../components/onboarding/CodeSnippetStep';
import SuccessStep from '../components/onboarding/SuccessStep';
import ProgressIndicator from '../components/onboarding/ProgressIndicator';

interface OnboardingData {
  siteName?: string;
  siteDomain?: string;
  siteId?: string;
  languages?: string[];
  integrationType?: 'sdk' | 'shopify' | 'woocommerce' | 'manual';
}

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({});
  const [loading, setLoading] = useState(false);
  const [showSkipWarning, setShowSkipWarning] = useState(false);

  const totalSteps = 6;

  useEffect(() => {
    loadOnboardingStatus();
  }, []);

  const loadOnboardingStatus = async () => {
    try {
      const { data } = await apiService.get('/onboarding/status');
      if (data.data.completed) {
        navigate('/dashboard');
      } else {
        if (data.data.step > 0) {
          setCurrentStep(data.data.step);
        }
        if (data.data.data) {
          setOnboardingData(data.data.data);
        }
      }
    } catch (error) {
      console.error('Failed to load onboarding status:', error);
    }
  };

  const saveProgress = async (step: number, data?: OnboardingData) => {
    try {
      await apiService.post('/onboarding/progress', {
        step,
        data: data || onboardingData,
      });
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  const handleNext = async (stepData?: Partial<OnboardingData>) => {
    const updatedData = { ...onboardingData, ...stepData };
    setOnboardingData(updatedData);

    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, updatedData);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setShowSkipWarning(true);
  };

  const confirmSkip = async () => {
    try {
      setLoading(true);
      await apiService.post('/onboarding/skip');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      setLoading(true);
      await apiService.post('/onboarding/complete');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep onNext={handleNext} />;
      case 2:
        return (
          <SiteSetupStep
            onNext={handleNext}
            onBack={handleBack}
            initialData={onboardingData}
          />
        );
      case 3:
        return (
          <LanguageStep
            onNext={handleNext}
            onBack={handleBack}
            initialData={onboardingData}
          />
        );
      case 4:
        return (
          <IntegrationStep
            onNext={handleNext}
            onBack={handleBack}
            initialData={onboardingData}
          />
        );
      case 5:
        return (
          <CodeSnippetStep
            onNext={handleNext}
            onBack={handleBack}
            onboardingData={onboardingData}
          />
        );
      case 6:
        return (
          <SuccessStep
            onComplete={handleComplete}
            onBack={handleBack}
            onboardingData={onboardingData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10">
      {/* Header */}
      <div className="bg-background border-b px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-primary">LocalizationHub</h1>
            <Badge variant="secondary" className="ml-4 bg-primary/10 text-primary">
              Setup Wizard
            </Badge>
          </div>

          {currentStep > 1 && currentStep < 6 && (
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-muted-foreground"
            >
              Skip for now
            </Button>
          )}
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-background border-b">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Card className="shadow-xl">
          {renderStep()}
        </Card>
      </div>

      {/* Skip Warning Modal */}
      {showSkipWarning && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <div className="flex items-start">
                <div className="flex-shrink-0 p-2 bg-yellow-100 rounded-full mr-4">
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">Skip Onboarding?</CardTitle>
                  <CardDescription className="mt-2">
                    We recommend completing the onboarding to get the most out of
                    LocalizationHub. You'll miss setting up your first site and
                    learning how to use key features.
                  </CardDescription>
                  <p className="mt-2 text-sm font-medium text-foreground">
                    You can always access this wizard later from Settings.
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowSkipWarning(false)}
                >
                  Continue Setup
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={confirmSkip}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Skipping...
                    </>
                  ) : (
                    'Skip Anyway'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
